<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { call } from '../api/http'
import { lineTrends, payables, type TrendPoint, type Vendor } from '../api/payables'
import { useSession } from '../stores/session'
import { useForm } from '../lib/form'
import { formatMoney } from '../lib/money'
import UiCard from '../components/UiCard.vue'
import UiSparkline from '../components/UiSparkline.vue'
import ErrorBanner from '../components/ErrorBanner.vue'
import EmptyState from '../components/EmptyState.vue'

const { t, locale } = useI18n()
const session = useSession()
const load = useForm()
const trendsLoad = useForm()

const vendors = ref<Vendor[]>([])
const vendor = ref('')
/** description ('' = all lines of the vendor) → monthly points */
const rows = ref<{ description: string; points: TrendPoint[] }[]>([])

// /vendors returns one row per vendor and currency.
const vendorNames = computed(() => [...new Set(vendors.value.map((v) => v.vendor))])

watch(
  () => session.workspace?.id,
  () =>
    load.submit(async () => {
      vendors.value = await call(payables.GET('/vendors'))
      vendor.value = vendorNames.value[0] ?? ''
    }),
  { immediate: true },
)

// There is no "distinct line descriptions" endpoint: collect them from the vendor's 12 latest bills.
// ponytail: 12 bills cover a year of monthly utilities; add a descriptions endpoint if vendors issue more line kinds.
async function loadTrends(name: string) {
  rows.value = []
  if (!name) return
  await trendsLoad.submit(async () => {
    const page = await call(payables.GET('/payables', { params: { query: { q: name, limit: 12 } } }))
    const bills = page.items.filter((p) => p.vendor.toLowerCase() === name.toLowerCase() && p.status !== 'cancelled')
    const lines = (await Promise.all(bills.map((p) => call(payables.GET('/payables/{id}/lines', { params: { path: { id: p.id } } }))))).flat()
    const descs = [...new Map(lines.map((l) => [l.description.toLowerCase(), l.description])).values()]
    const all = await Promise.all(['', ...descs].map(async (d) => ({ description: d, points: await lineTrends(name, d || undefined) })))
    rows.value = all.filter((r) => r.points.length)
  })
}
watch(vendor, loadTrends)

/** Points of one currency only (the latest one's), so the sparkline compares like with like. */
const series = (pts: TrendPoint[]) => {
  const cur = pts.at(-1)?.amount.currency
  return pts.filter((p) => p.amount.currency === cur)
}
const num = (s?: string) => (s == null ? null : Number(s))
const last = (pts: TrendPoint[]) => series(pts).at(-1)
const fmtPrice = (s?: string) => (s == null ? '—' : Number(s).toLocaleString(locale.value, { maximumFractionDigits: 4 }))
const range = (pts: TrendPoint[]) => (pts.length ? `${pts[0]!.month.slice(0, 7)} – ${pts.at(-1)!.month.slice(0, 7)}` : '')
</script>

<template>
  <div class="page wide">
    <header class="page-header">
      <div>
        <h1>{{ $t('utilities.title') }}</h1>
        <p class="muted">{{ $t('utilities.subtitle') }}</p>
      </div>
    </header>

    <ErrorBanner :error="load.error.value ?? trendsLoad.error.value" />

    <UiCard v-if="!load.busy.value && !vendorNames.length">
      <EmptyState :title="$t('utilities.empty')" :message="$t('utilities.emptyText')">
        <RouterLink to="/documents">{{ $t('nav.documents') }}</RouterLink>
      </EmptyState>
    </UiCard>

    <UiCard v-else>
      <label class="vendor">
        <span class="muted small">{{ $t('bills.vendor') }}</span>
        <select v-model="vendor" data-testid="utility-vendor">
          <option v-for="v in vendorNames" :key="v" :value="v">{{ v }}</option>
        </select>
      </label>

      <p v-if="trendsLoad.busy.value" class="muted">{{ $t('common.loading') }}</p>
      <p v-else-if="vendor && !rows.length" class="muted small">{{ $t('utilities.noLines') }}</p>
      <div v-else-if="rows.length" class="table-wrap">
        <table class="table" data-testid="utility-trends">
          <thead>
            <tr>
              <th>{{ $t('bills.description') }}</th>
              <th>{{ $t('utilities.months') }}</th>
              <th class="num">{{ $t('utilities.lastAmount') }}</th>
              <th>{{ $t('utilities.amountTrend') }}</th>
              <th class="num">{{ $t('utilities.lastUnitPrice') }}</th>
              <th>{{ $t('utilities.priceTrend') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in rows" :key="r.description" data-testid="utility-row">
              <td>
                <strong v-if="!r.description">{{ $t('utilities.allLines') }}</strong>
                <span v-else>{{ r.description }}</span>
              </td>
              <td class="small muted">{{ range(series(r.points)) }}</td>
              <td class="num">{{ last(r.points) ? formatMoney(last(r.points)!.amount, locale) : '—' }}</td>
              <td>
                <UiSparkline :values="series(r.points).map((p) => p.amount.amount_minor)" :label="t('utilities.amountTrend')" />
              </td>
              <td class="num">{{ fmtPrice(last(r.points)?.unit_price) }}</td>
              <td>
                <UiSparkline :values="series(r.points).map((p) => num(p.unit_price))" :label="t('utilities.priceTrend')" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </UiCard>
  </div>
</template>

<style scoped>
.wide {
  max-width: 1200px;
}
.vendor {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  max-width: 360px;
  margin-bottom: 1rem;
}
</style>
